import { useEffect, useRef } from 'react'

import { useApi } from '@core/api'
import { GetV1ItemOutput, LegacyItemState } from '@core/api/generated'
import { useQuery, useQueryClient } from '@tanstack/react-query'

export const itemPollerKeys = {
  item: (itemId: string) => ['v1-item', itemId] as const,
}

type PrevStates = Record<string, LegacyItemState>

type ItemPollerProps = {
  itemId: string
  enabled?: boolean
  onDone?: (itemId: string, data: GetV1ItemOutput, prevState: LegacyItemState) => void
  onFail?: (itemId: string, data: GetV1ItemOutput, prevState: LegacyItemState) => void
  onTimeout?: (itemId: string, data: GetV1ItemOutput) => void
}

const checkTransitions = (
  itemId: string,
  current: LegacyItemState,
  prev: LegacyItemState | undefined,
  data: GetV1ItemOutput,
  callbacks: Pick<ItemPollerProps, 'onDone' | 'onFail' | 'onTimeout'>,
) => {
  const { onDone, onFail, onTimeout } = callbacks

  if (!prev?.isTimedOut && current.isTimedOut) {
    onTimeout?.(itemId, data)
  } else if (prev && prev.status !== 'DONE' && current.status === 'DONE') {
    onDone?.(itemId, data, prev)
  } else if (prev && prev.status !== 'FAILED' && current.status === 'FAILED') {
    onFail?.(itemId, data, prev)
  }
}

const hasAnyLoading = (data: GetV1ItemOutput): boolean => {
  if (data.itemState.isLoading) return true
  return data.childrenStates.some((child) => child.itemState.isLoading)
}

export const ItemPoller = ({
  itemId,
  enabled = true,
  onDone,
  onFail,
  onTimeout,
}: ItemPollerProps) => {
  const api = useApi()
  const prevStatesRef = useRef<PrevStates>({})

  const { data } = useQuery({
    queryKey: itemPollerKeys.item(itemId),
    queryFn: () => api.getV1Item({ path: { item_id: itemId } }).then((r) => r.data!),
    enabled,
    refetchInterval: ({ state }) => {
      return state.data && hasAnyLoading(state.data) ? 2000 : false
    },
  })

  useEffect(() => {
    if (!data) return

    const prevStates = prevStatesRef.current
    const callbacks = { onDone, onFail, onTimeout }

    checkTransitions(itemId, data.itemState, prevStates[itemId], data, callbacks)

    for (const child of data.childrenStates) {
      checkTransitions(child.id, child.itemState, prevStates[child.id], data, callbacks)
      prevStates[child.id] = child.itemState
    }

    prevStates[itemId] = data.itemState
  }, [data, itemId, onDone, onFail, onTimeout])

  return null
}

export const useInvalidateItemPoller = () => {
  const queryClient = useQueryClient()

  return (itemId: string) =>
    queryClient.invalidateQueries({ queryKey: itemPollerKeys.item(itemId) })
}
